/**
 * JournalEntriesColumns — column definitions for the journal entries table.
 *
 * Separated from the page component so the column array itself stays short and
 * testable in isolation.
 */

import { Badge, Skeleton } from '@elsapiens/ui'


import {
  hasForeignCurrency,
  isBogusTotal,
  formatMoney,
  formatDate,
  getStatusBadgeVariant,
  ENTRY_STATUS_LABELS,
  type DisplayEntry,
  type EntryStatus,
} from './journal-entries-helpers'
import { getEntryTypeLabel, type EntryType } from '../../services/journal-entry-service'

import type { TableColumn } from '@elsapiens/ui'

// ── Skeleton helper ───────────────────────────────────────────────────────────

/**
 * The badge-height CSS expression is reused across all skeleton cells so every
 * column skeleton matches the tallest real data cell (the status badge).
 */
export const BADGE_HEIGHT = 'calc(var(--el-badge-font-size-sm) * 1.5 + var(--el-badge-py-sm) * 2)'

export function buildSkeletonColumns(): TableColumn<Record<string, unknown>>[] {
  return [
    { key: 'a', header: 'Entry #', width: '100px', render: () => <Skeleton height={BADGE_HEIGHT} width="70px" /> },
    { key: 'b', header: 'Date', width: '120px', render: () => <Skeleton height={BADGE_HEIGHT} width="90px" /> },
    { key: 'c', header: 'Type', width: '120px', render: () => <Skeleton height={BADGE_HEIGHT} width="70px" /> },
    { key: 'd', header: 'Memo', render: () => <Skeleton height={BADGE_HEIGHT} width="150px" /> },
    { key: 'e', header: 'Debit', width: '140px', align: 'right', render: () => <Skeleton className="ml-auto" height={BADGE_HEIGHT} width="80px" /> },
    { key: 'f', header: 'Credit', width: '140px', align: 'right', render: () => <Skeleton className="ml-auto" height={BADGE_HEIGHT} width="80px" /> },
    { key: 'g', header: 'Status', width: '100px', render: () => <Skeleton className="rounded-md" height={BADGE_HEIGHT} width="60px" /> },
  ]
}

export function buildSkeletonData(): Record<string, unknown>[] {
  return Array.from({ length: 10 }, (_, i) => ({ id: String(i) }))
}

// ── Amount cell renderer ──────────────────────────────────────────────────────

interface AmountCellProps {
  txn: { amount: number; currency: string }
  base: { amount: number; currency: string } | undefined
  baseCurrency: string
  currencyView: 'transaction' | 'base'
  hasFx: boolean
}

export function AmountCell({ txn, base, baseCurrency, currencyView, hasFx }: AmountCellProps): JSX.Element {
  const isBogus = isBogusTotal(txn, base, baseCurrency)
  const shouldShowBase = currencyView === 'base' || isBogus
  return (
    <div className="flex items-center justify-end gap-1.5 leading-normal">
      <span className="font-mono leading-[inherit]">
        {shouldShowBase
          ? formatMoney(base?.amount ?? txn.amount, base?.currency ?? baseCurrency)
          : formatMoney(txn.amount, txn.currency)}
      </span>
      {hasFx && (
        <span
          aria-label="Foreign currency"
          className="text-blue-600 dark:text-blue-400"
          title="Contains foreign currency transactions"
        >
          🌐
        </span>
      )}
    </div>
  )
}

// ── Column definitions ────────────────────────────────────────────────────────

function buildAmountColumns(
  baseCurrency: string,
  currencyView: 'transaction' | 'base',
): TableColumn<DisplayEntry>[] {
  return [
    {
      key: 'totalDebit', header: 'Debit', width: '140px', align: 'right',
      render: (entry) => (
        <AmountCell
          base={entry.totalDebitBase}
          baseCurrency={baseCurrency}
          currencyView={currencyView}
          hasFx={hasForeignCurrency(entry, baseCurrency)}
          txn={entry.totalDebit}
        />
      ),
    },
    {
      key: 'totalCredit', header: 'Credit', width: '140px', align: 'right',
      render: (entry) => (
        <AmountCell
          base={entry.totalCreditBase}
          baseCurrency={baseCurrency}
          currencyView={currencyView}
          hasFx={hasForeignCurrency(entry, baseCurrency)}
          txn={entry.totalCredit}
        />
      ),
    },
  ]
}

export function buildEntryColumns(
  baseCurrency: string,
  currencyView: 'transaction' | 'base',
  locale: string,
): TableColumn<DisplayEntry>[] {
  return [
    { key: 'entryNumber', header: 'Entry #', width: '100px', className: 'font-medium' },
    { key: 'entryDate', header: 'Date', width: '120px', render: (entry) => formatDate(entry.entryDate, locale) },
    { key: 'entryType', header: 'Type', width: '120px', render: (entry) => getEntryTypeLabel(entry.entryType as EntryType) },
    { key: 'memo', header: 'Memo', render: (entry) => entry.memo ?? '-' },
    ...buildAmountColumns(baseCurrency, currencyView),
    {
      key: 'status', header: 'Status', width: '100px',
      render: (entry) => (
        <Badge size="sm" variant={getStatusBadgeVariant(entry.status)}>
          {ENTRY_STATUS_LABELS[entry.status as EntryStatus] ?? entry.status}
        </Badge>
      ),
    },
  ]
}
